import lockIcon from '@assets/svg/LockIcon.svg';
import { CodeInput, CustomButton, CustomDrawer, PhoneNumberInput } from '@components';
import { HttpsOutlined } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { COLORS } from '@styles';
import { alpha2ToPhoneCountryCode, appContext } from '@utils';
import { FormikProps, useFormik } from 'formik';
import { CSSProperties, useContext, useEffect, useState } from 'react';
import { formatPhoneNumber } from 'react-phone-number-input';
import * as Yup from 'yup';

const styles = {
  image: {
    width: "8.5rem",
  } as CSSProperties,
}

interface UpdatePhoneStepsProps {
  formik: FormikProps<UpdatePhoneDrawerFormValues>;
}

function UpdatePhoneNumberStep({ formik }: UpdatePhoneStepsProps) {
  const {
    userStore: {
      resendPhoneChangeCode,
      phoneChangeResponse,
    },
  } = useContext(appContext);

  const { values, handleSubmit } = formik;

  const handleResendCode = async () => {
    await resendPhoneChangeCode({
      phone: formik.values.phoneNumber,
      countryCodeAlpha2: formik.values.countryCode,
    });
  };

  const isResending = phoneChangeResponse.status === 'pending';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '90%' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img
          src={lockIcon}
          alt=""
          aria-hidden
          style={styles.image}
        />
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <Typography component="p" style={{
            fontFamily: "urbanist",
            fontSize: "1.25rem",
            fontWeight: 700,
            textAlign: "center",
            marginBottom: "1.5rem"
          }}>
            Enter Verification Code
          </Typography>
          <Typography
            component="p"
            style={{
              fontSize: '0.875rem',
              fontWeight: 400,
              textAlign: 'center',
              marginBottom: '2rem',
            }}
          >
            Please enter the <b>6-digit code</b> that we sent to (***) ***-{values.phoneNumber.slice(-4)} to finish the
            phone verification and proceed with the change.
          </Typography>
        </Box>

        <Box>
          <Box>
            <CodeInput
              value={values.code}
              onChange={(value) => formik.setFieldValue('code', value)}
              variant="dark"
            />
            <Button
              sx={{
                color: COLORS.ELEKTRAFI_NEUTRAL,
                width: '100%',
                height: '2.5rem',
                marginTop: '1rem',
              }}
              type="button"
              onClick={handleResendCode}
              disabled={formik.isSubmitting || isResending}
            >
              Resend Code
            </Button>
          </Box>
        </Box>
      </Box>

      <Box sx={{ marginTop: 'auto', paddingInline: '0.5rem' }}>
        <CustomButton
          variant="default"
          type="submit"
          isLoading={formik.isSubmitting || isResending}
          disabled={formik.isSubmitting || isResending}
          onClick={() => handleSubmit()}
        >
          Verify Code
        </CustomButton>
      </Box>
    </Box>
  )
}

function ConfirmNewPhoneNumberStep({ formik }: UpdatePhoneStepsProps) {
  const { values, handleSubmit } = formik;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '90%' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img
          src={lockIcon}
          alt=""
          aria-hidden
          style={styles.image}
        />
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <Typography component="p" style={{
            fontFamily: "urbanist",
            fontSize: "1.25rem",
            fontWeight: 700,
            textAlign: "center",
            marginBottom: "1.5rem"
          }}>
            Current phone verified!<br />Now let&apos;s confirm your new phone number to complete the change.
          </Typography>
          <Typography
            component="p"
            style={{
              fontSize: '0.875rem',
              fontWeight: 400,
              textAlign: 'center',
              marginBottom: '2rem',
            }}
          >
            We’ll send a verification code to your new number. This helps keep your account secure and ensures you receive important notifications about your finances and milestones.
          </Typography>
        </Box>

        <PhoneNumberInput
          value={values.phoneNumber}
          onChange={(value) => formik.setFieldValue('phoneNumber', value)}
          variant="dark"
          country={values.countryCode}
          onCountryChange={(country) => formik.setFieldValue('countryCode', country)}
          label="New Phone Number"
          name="phoneNumber"
          inputId="phoneNumber"
        />
      </Box>

      <Box sx={{ marginTop: 'auto', paddingInline: '0.5rem' }}>
        <CustomButton
          variant="default"
          type="submit"
          isLoading={formik.isSubmitting}
          disabled={formik.isSubmitting}
          onClick={() => handleSubmit()}
        >
          Send Code
        </CustomButton>
      </Box>
    </Box>
  )
}

const codeValidationSchema = Yup.object({
  code: Yup.string()
    .required('Verification code is required')
});

interface UpdatePhoneDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  newPhoneNumber: string;
}

enum UpdatePhoneDrawerStep {
  UpdatePhoneNumber = 'UpdatePhoneNumber',
  ConfirmNewPhoneNumber = 'confirmNewPhoneNumber',
}

interface UpdatePhoneDrawerFormValues {
  code: string;
  phoneNumber: string;
  countryCode: string;
}

/**
 * Drawer for updating the current phone number: enter new number → receive code → verify and apply.
 */
export default function UpdatePhoneDrawer({
  isOpen,
  onClose,
  newPhoneNumber,
}: UpdatePhoneDrawerProps) {
  const {
    userStore: {
      requestPhoneChange,
      verifyPhoneChange,
    },
  } = useContext(appContext);

  const [step, setStep] = useState<UpdatePhoneDrawerStep>(UpdatePhoneDrawerStep.ConfirmNewPhoneNumber);

  const initialValues: UpdatePhoneDrawerFormValues = { code: '', phoneNumber: newPhoneNumber, countryCode: 'CA' };

  const formik = useFormik<UpdatePhoneDrawerFormValues>({
    initialValues,
    validationSchema: step === UpdatePhoneDrawerStep.UpdatePhoneNumber ? codeValidationSchema : undefined,
    onSubmit: async (formValues) => {
      if (step === UpdatePhoneDrawerStep.ConfirmNewPhoneNumber) {
        const success = await requestPhoneChange({
          phone: formatPhoneNumber(formValues.phoneNumber),
          countryCodeAlpha2: formValues.countryCode,
        });
        if (success) setStep(UpdatePhoneDrawerStep.UpdatePhoneNumber);
      } else {
        const phoneCountryCode = alpha2ToPhoneCountryCode(formValues.countryCode);
        if (!phoneCountryCode) return;
        const success = await verifyPhoneChange({
          code: formValues.code,
          phone: formatPhoneNumber(formValues.phoneNumber),
          phoneCountryCode,
        });
        if (success) {
          onClose();
          formik.resetForm();
        }
      }
    },
    validateOnChange: false,
    validateOnBlur: false,
    enableReinitialize: true,
  });

  useEffect(() => {
    if (isOpen) {
      setStep(UpdatePhoneDrawerStep.ConfirmNewPhoneNumber);
      formik.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <CustomDrawer
      open={isOpen}
      onClose={onClose}
      headerLabel="Confirm your New Phone Number"
      headerIcon={<HttpsOutlined sx={{ color: COLORS.ELEKTRAFI_CYAN_NEUTRAL }} />}
      sx={{
        zIndex: 1400,
      }}
    >
      {step === UpdatePhoneDrawerStep.ConfirmNewPhoneNumber && <ConfirmNewPhoneNumberStep formik={formik} />}
      {step === UpdatePhoneDrawerStep.UpdatePhoneNumber && (
        <UpdatePhoneNumberStep
          formik={formik}
        />
      )}
    </CustomDrawer>
  );
}


